import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Millwright,
  MillwrightKPI,
} from '@/apis/millwrights/types/millwrightsApi';
import {
  millwrightFromApiAdapter,
  millwrightKpisFromApiAdapter,
  outageMillwrightsFromApiAdapter,
} from '@/apis/millwrights/adapters/millwrightsFromApiAdapter';
import { prepareHeadersWithAuthorization as prepareHeaders } from '@/apis/headers';
import { PaginatedResponse } from '@/types';

const BASE_URL = import.meta.env.VITE_APP_API_URL;

export interface MillrightsByOutageParams {
  outageId: number;
  q?: string;
  limit?: number;
  offset?: number;
  sortDir?: 'asc' | 'desc';
  sortBy?: 'Name' | 'Risk';
}

export interface MillwrightByIdParams {
  empId: number;
  outageId?: number;
}

export interface MillrightsByTaskParams {
  outageId: number;
  taskId: string;
}

export interface MillwrightKPIsParams {
  empId: number;
  outageId: number | null;
  topN?: number;
  excludeNames?: string[] | null;
}

export interface MillrightsParams {
  limit?: number;
  offset?: number;
  q?: string;
  sortDir?: 'asc' | 'desc';
  sortBy?: 'name' | 'risk' | 'Name' | 'Risk';
  riskMin?: number;
  riskMax?: number;
}

export const millwrightsApi = createApi({
  reducerPath: 'millwrightsApi',
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL, prepareHeaders }),
  endpoints: (builder) => ({
    getMillwrights: builder.query<
      PaginatedResponse<Millwright>,
      MillrightsParams
    >({
      query: ({ limit, offset, q, sortDir, sortBy, riskMin, riskMax }) => ({
        url: '/active_millwrights',
        params: {
          ...(q && { q }),
          ...(limit && { limit }),
          ...(offset && { offset }),
          ...(sortDir && { sort_dir: sortDir }),
          ...(sortBy && { sort_by: sortBy }),
          ...(riskMin && { risk_min: riskMin }),
          ...(riskMax && { risk_max: riskMax }),
        },
      }),
      transformResponse: outageMillwrightsFromApiAdapter,
    }),
    getMillwrightById: builder.query<Millwright, MillwrightByIdParams>({
      query: ({ empId, outageId }) => ({
        url: `/employees/${empId}`,
        params: {
          ...(outageId && { outage_id: outageId }),
        },
      }),
      transformResponse: millwrightFromApiAdapter,
    }),
    getMillwrightsByOutageId: builder.query<
      PaginatedResponse<Millwright>,
      MillrightsByOutageParams
    >({
      query: ({ outageId, q, limit, offset, sortDir, sortBy }) => ({
        url: `/hours-worked/outage/${outageId}`,
        params: {
          ...(q && { q }),
          ...(limit && { limit }),
          ...(offset && { offset }),
          ...(sortDir && { sort_dir: sortDir }),
          ...(sortBy && { sort_by: sortBy }),
        },
      }),
      transformResponse: outageMillwrightsFromApiAdapter,
    }),
    getMillwrightsByOutageIdAndTaskId: builder.query<
      PaginatedResponse<Millwright>,
      MillrightsByTaskParams
    >({
      query: ({ outageId, taskId }) => ({
        url: `/hours-worked/outage/${outageId}/tasks/${taskId}`,
      }),
      transformResponse: outageMillwrightsFromApiAdapter,
    }),
    getMillwrightKPIs: builder.query<MillwrightKPI[], MillwrightKPIsParams>({
      query: ({ empId, outageId, excludeNames, topN }) => ({
        url: '/kpis/latest-top',
        params: {
          emp_id: empId,
          outage_id: outageId,
          ...(topN && { top_n: topN }),
          ...(excludeNames?.length && {
            exclude_names: excludeNames.join(','),
          }),
        },
      }),
      transformResponse: millwrightKpisFromApiAdapter,
    }),
  }),
});

export const {
  useGetMillwrightsQuery,
  useGetMillwrightByIdQuery,
  useGetMillwrightsByOutageIdQuery,
  useGetMillwrightsByOutageIdAndTaskIdQuery,
  useGetMillwrightKPIsQuery,
} = millwrightsApi;
